import { ExerciseIntervalDto } from '@/generated';
import { numberValue } from '@/utils/number-utils';
import { ArrowUpward, ArrowDownward, Remove, Add } from '@mui/icons-material';
import { Dialog, DialogTitle, DialogContent, Stack, TextField, Tooltip, IconButton, DialogActions, Button } from '@mui/material';
import { useState } from 'react';

interface IntervalDialogProps {
  intervals: ExerciseIntervalDto[];
  open: boolean;
  onCancel: () => void;
  onSave: (intervals: ExerciseIntervalDto[]) => void;
}

export const IntervalDialog = (props: IntervalDialogProps) => {
  const [intervals, setIntervals] = useState<ExerciseIntervalDto[]>(props.intervals);

  const updateInterval = (index: number, changes: Partial<ExerciseIntervalDto>) => {
    setIntervals((current) => current.map((interval, intervalIndex) => (intervalIndex === index ? { ...interval, ...changes } : interval)));
  };

  const moveInterval = (index: number, offset: number) => {
    setIntervals((current) => {
      const destination = index + offset;
      if (destination < 0 || destination >= current.length) return current;
      const reordered = [...current];
      [reordered[index], reordered[destination]] = [reordered[destination], reordered[index]];
      return reordered;
    });
  };

  return (
    <Dialog open={props.open} onClose={props.onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Edit intervals</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {intervals.map((interval, index) => (
            <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <TextField
                label="Duration seconds"
                type="number"
                fullWidth
                value={interval.durationSeconds}
                onChange={(event) => updateInterval(index, { durationSeconds: numberValue(event.target.value) })}
              />
              <TextField
                label="Pause seconds"
                type="number"
                fullWidth
                value={interval.pauseAfterSeconds ?? 0}
                onChange={(event) => updateInterval(index, { pauseAfterSeconds: numberValue(event.target.value) })}
              />
              <Stack direction="row" sx={{ flexShrink: 0 }}>
                <Tooltip title="Move interval up">
                  <span>
                    <IconButton aria-label={`Move interval ${index + 1} up`} disabled={index === 0} onClick={() => moveInterval(index, -1)}>
                      <ArrowUpward />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move interval down">
                  <span>
                    <IconButton aria-label={`Move interval ${index + 1} down`} disabled={index === intervals.length - 1} onClick={() => moveInterval(index, 1)}>
                      <ArrowDownward />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Remove interval">
                  <IconButton aria-label={`Remove interval ${index + 1}`} onClick={() => setIntervals((current) => current.filter((_, itemIndex) => itemIndex !== index))}>
                    <Remove />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          ))}
          <Tooltip title="Add interval">
            <IconButton
              aria-label="Add interval"
              sx={{ alignSelf: 'flex-start', color: 'common.white' }}
              onClick={() =>
                setIntervals((current) => [
                  ...current.map((interval, index) => (index === current.length - 1 ? { ...interval, pauseAfterSeconds: 5 } : interval)),
                  { durationSeconds: 10, pauseAfterSeconds: 0 },
                ])
              }
            >
              <Add />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel}>Cancel</Button>
        <Button variant="contained" onClick={() => props.onSave(intervals)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
